import React, { useEffect, useState } from "react";
import {
  Card,
  Typography,
  Table,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Descriptions,
  Switch,
  Button,
  Modal,
  Tooltip,
  Tag,
  Dropdown,
} from "antd";
import { EyeOutlined, DeleteOutlined, ExclamationCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, StopOutlined, CloseOutlined, DownOutlined, WarningOutlined } from "@ant-design/icons";
import moment from "moment";
import useRequest from "../hooks/useRequest";
import apiPath from "../constants/apiPath";
import { Severty, ShowToast } from "../helper/toast";

const { RangePicker } = DatePicker;
const Search = Input.Search;
const withTooltip = (value) => {
  const text = value == null || value === "" ? "—" : String(value);
  return (
    <Tooltip title={text}>
      <span>{text}</span>
    </Tooltip>
  );
};

const UserManager = () => {
  const { request } = useRequest();

  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const [searchKeyword, setSearchKeyword] = useState("");
  const [appliedKeyword, setAppliedKeyword] = useState("");

  const [filters, setFilters] = useState({
    status: "all", // all | active | inactive | deleted
    dateRange: undefined, // [moment, moment]
  });

  const [selectedUser, setSelectedUser] = useState(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [cancelingSubUserId, setCancelingSubUserId] = useState(null);
  const [deletedUserIds, setDeletedUserIds] = useState(new Set());

  const isUserDeleted = (u) => !!u?.isDeleted || deletedUserIds.has(u?.id);

  const getUserStatus = (u) => {
    if (isUserDeleted(u)) return "deleted";
    const isActive = u?.isActive !== undefined ? !!u?.isActive : true;
    return isActive ? "active" : "inactive";
  };

  const handleCancelSubscription = (user) => {
    Modal.confirm({
      className: "user-delete-confirm-modal",
      title: "Cancel Subscription?",
      content: (
        <div style={{ color: "#4b5563", marginTop: 8 }}>
          This will cancel the subscription at the end of the current billing period. The user will continue to have access until that date. The subscription will not renew.
        </div>
      ),
      okText: "Yes, Cancel at Period End",
      okButtonProps: { danger: true },
      cancelText: "No",
      onOk: async () => {
        setCancelingSubUserId(user.id);
        try {
          await request({
            url: `${apiPath.authAdminUsersList}/${user.id}/subscription/cancel`,
            method: "POST",
            onSuccess: (data) => {
              ShowToast(data.message || "Subscription set to cancel at period end.", Severty.SUCCESS);
              // Update local state so UI reflects immediately without full refresh if desired
              // But easiest is just to re-fetch users or update the selectedUser object
              setSelectedUser((prev) =>
                prev?.id === user.id ? { ...prev, cancelAtPeriodEnd: true } : prev
              );
              fetchUsers();
            },
            onError: (err) => {
              ShowToast(err?.response?.data?.message || err.message || "Failed to cancel subscription", Severty.ERROR);
            },
          });
        } finally {
          setCancelingSubUserId(null);
        }
      },
    });
  };

  const statusLabel = (u) => {
    const status = getUserStatus(u);
    if (status === "deleted") return "Deleted";
    if (status === "inactive") return "Inactive";
    return "Active";
  };

  const buildQueryParams = () => {
    const queryParams = new URLSearchParams();
    queryParams.append("page", pagination.current);
    queryParams.append("limit", pagination.pageSize);
    if (appliedKeyword?.trim()) queryParams.append("q", appliedKeyword.trim());
    if (filters.status && filters.status !== "all") queryParams.append("status", filters.status);
    if (filters.dateRange && filters.dateRange.length === 2) {
      queryParams.append("from", filters.dateRange[0].format("YYYY-MM-DD"));
      queryParams.append("to", filters.dateRange[1].format("YYYY-MM-DD"));
    }
    return queryParams.toString();
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const url = `${apiPath.authAdminUsersList}?${buildQueryParams()}`;
      await request({
        url,
        method: "GET",
        onSuccess: (data) => {
          const nextUsers = data?.users || [];
          setUsers(nextUsers);
          setTotalUsers(data?.totalUsers || 0);
          // Keep selected user if still present; otherwise clear.
          if (selectedUser?.id) {
            const stillExists = nextUsers.some((u) => u.id === selectedUser.id);
            if (!stillExists) setSelectedUser(null);
          }
        },
        onError: (err) => {
          console.error(err);
          ShowToast("Failed to fetch users", Severty.ERROR);
        },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, appliedKeyword, filters.status, filters.dateRange]);

  const handleViewUser = async (record) => {
    if (!record?.id) return;
    // Open quickly with table data, then load full user from API.
    setSelectedUser(record);
    await request({
      url: `${apiPath.authAdminUserById}/${record.id}`,
      method: "GET",
      onSuccess: (data) => {
        if (data?.user) setSelectedUser(data.user);
      },
      onError: (err) => {
        const msg =
          err?.response?.data?.message || err?.message || "Failed to load user";
        ShowToast(msg, Severty.ERROR);
      },
    });
  };

  const handleDeleteUser = async (record) => {
    if (!record?.id) return;
    setDeletingUserId(record.id);
    try {
      const response = await request({
        url: `${apiPath.authAdminUserById}/${record.id}`,
        method: "DELETE",
        onError: () => {},
      });

      if (response) {
        ShowToast("User deleted successfully", Severty.SUCCESS);
        fetchUsers();
        if (selectedUser?.id === record.id) setSelectedUser(null);
      } else {
        ShowToast("Failed to delete user", Severty.ERROR);
      }
    } finally {
      setDeletingUserId(null);
    }
  };

  const confirmDeleteUser = (record) => {
    Modal.confirm({
      className: "user-delete-confirm-modal",
      icon: <ExclamationCircleOutlined style={{ color: "#FAAD14" }} />,
      title: (
        <span style={{ color: "#1f2937", fontWeight: 600 }}>
          {"Delete user?"}
        </span>
      ),
      content: (
        <div style={{ color: "#4b5563", marginTop: 8 }}>
          {"This user will be marked as deleted and hidden from the main list."}
        </div>
      ),
      okText: "Delete",
      cancelText: "Cancel",
      okButtonProps: { danger: true },
      centered: true,
      onOk: () => handleDeleteUser(record),
    });
  };

  const handleApplySearch = (value) => {
    setAppliedKeyword(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleClear = () => {
    setAppliedKeyword("");
    setSearchKeyword("");
    setFilters({ status: "all", dateRange: undefined });
    setPagination({ current: 1, pageSize: 10 });
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (v) => withTooltip(v),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (v) => withTooltip(v),
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      render: (v) => withTooltip(v),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (v) => withTooltip(v),
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => {
        const isDeleted = isUserDeleted(record);
        const checked = getUserStatus(record) === "active";
        const isAdmin = String(record?.role || "").toLowerCase() === "admin";
        const adminActiveLock = isAdmin && checked;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "center" }}>
            <Tooltip
              title={
                adminActiveLock
                  ? "Admin accounts cannot be set inactive from here"
                  : undefined
              }
            >
            <Switch
              checked={checked}
              disabled={isDeleted || adminActiveLock}
              loading={statusUpdatingId === record.id}
              onChange={(nextChecked) => {
                // Only toggle Active/Inactive. If deleted, switch is disabled.
                const nextStatus = nextChecked ? "active" : "inactive";
                (async () => {
                  setStatusUpdatingId(record.id);
                  try {
                    await request({
                      url: `${apiPath.authAdminUserStatusUpdate}/${record.id}/status`,
                      method: "PATCH",
                      data: { status: nextStatus },
                      onSuccess: (data) => {
                        ShowToast(
                          `User status updated to ${nextStatus}`,
                          Severty.SUCCESS
                        );
                        fetchUsers();
                        if (selectedUser?.id === record.id && data?.user) {
                          setSelectedUser((prev) => ({
                            ...(prev || {}),
                            ...data.user,
                          }));
                        }
                      },
                      onError: (err) => {
                        const msg =
                          err?.response?.data?.message || err?.message || "Failed to update status";
                        ShowToast(msg, Severty.ERROR);
                      },
                    });
                  } finally {
                    setStatusUpdatingId(null);
                  }
                })();
              }}
            />
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: "Signup Date",
      key: "createdAt",
      render: (_, record) => (record?.createdAt ? moment(record.createdAt).format("YYYY-MM-DD") : "—"),
    },
    {
      title: "Plan",
      dataIndex: "plan",
      key: "plan",
      render: (v) => withTooltip(v || "None"),
    },

    {
      title: "Trial End",
      dataIndex: "trialEnd",
      key: "trialEnd",
      render: (v) => (v ? moment(v).format("YYYY-MM-DD") : "—"),
    },
    {
      title: "Subscription Status",
      dataIndex: "subscriptionStatus",
      key: "subscriptionStatus",
      render: (status, record) => {
        let color = "default";
        if (status === "active") color = "green";
        else if (status === "trialing") color = "blue";
        else if (status === "canceled") color = "red";
        else if (status === "past_due") color = "orange";
        const isClickable = record.billingActive && !record.cancelAtPeriodEnd;
        
        const tag = (
          <Tag color={color} style={{ margin: 0, cursor: isClickable ? "pointer" : "default" }}>
            {status?.toUpperCase() || "NONE"}
          </Tag>
        );

        if (isClickable) {
          const items = [
            {
              key: '1',
              style: {
                backgroundColor: "rgba(230, 126, 61, 0.1)", // Light version of #E67E3D
              },
              icon: <WarningOutlined style={{ fontSize: '15px', color: '#E67E3D', fontWeight: 'bold' }} />,
              label: <span style={{ fontWeight: 'bold', color: '#E67E3D' }}>Cancel Subscription</span>,
              onClick: (e) => {
                e.domEvent.stopPropagation();
                handleCancelSubscription(record);
              }
            }
          ];
          return (
            <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight" arrow>
              <div 
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', cursor: 'pointer', paddingRight: '4px' }}
                onClick={e => e.stopPropagation()}
              >
                {tag}
                <DownOutlined style={{ fontSize: "12px", color: "#8c8c8c" }} />
              </div>
            </Dropdown>
          );
        }
        
        return tag;
      },
    },
    {
      title: "Stripe Customer",
      dataIndex: "stripeCustomer",
      key: "stripeCustomer",
      render: (v) => (v ? <span style={{ fontSize: "12px" }}>{v.substring(0, 10)}...</span> : "—"),
    },
    {
      title: "Billing Active",
      dataIndex: "billingActive",
      key: "billingActive",
      render: (v) => (v ? <CheckCircleOutlined style={{ color: "green" }} /> : <CloseCircleOutlined style={{ color: "#ccc" }} />),
    },
    {
      title: "Cancel At End",
      dataIndex: "cancelAtPeriodEnd",
      key: "cancelAtPeriodEnd",
      render: (v) => (v ? <CheckCircleOutlined style={{ color: "orange" }} /> : <CloseCircleOutlined style={{ color: "#ccc" }} />),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 8,
            height: "100%",
          }}
        >
          <Button
            type="primary"
            className="table-view-button"
            icon={<EyeOutlined  />} 
            onClick={(e) => {
              e.stopPropagation();
              handleViewUser(record);
            }}
          />

          <Tooltip
            title={
              String(record?.role || "").toLowerCase() === "admin"
                ? "Admin accounts cannot be deleted"
                : undefined
            }
          >
            <Button
              type="text"
              className="table-delete-button"
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                confirmDeleteUser(record);
              }}
              loading={deletingUserId === record.id}
              disabled={
                deletingUserId === record.id ||
                String(record?.role || "").toLowerCase() === "admin"
              }
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const displayedUsers = users.filter((u) => {
    const status = getUserStatus(u);
    if (filters.status === "all") return status !== "deleted";
    return status === filters.status;
  });

  return (
    <Card bordered={false} className="criclebox" title="User Manager">
      <div style={{ marginBottom: 16 }}>
        <Typography.Text >
          Total users: {totalUsers}
        </Typography.Text>
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <Search
          size="large"
          placeholder="Quick search (Name, Email, Username)"
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          onSearch={handleApplySearch}
          style={{ width: 320 }}
          allowClear
        />

        <Select
          placeholder="Account status"
          value={filters.status}
          style={{ width: 160 }}
          onChange={(value) => {
            setFilters((prev) => ({ ...prev, status: value }));
            setPagination((p) => ({ ...p, current: 1 }));
          }}
        >
          <Select.Option value="all">All</Select.Option>
          <Select.Option value="active">Active</Select.Option>
          <Select.Option value="inactive">Inactive</Select.Option>
        </Select>

        <RangePicker
          value={filters.dateRange}
          onChange={(dates) => {
            setFilters((prev) => ({ ...prev, dateRange: dates }));
            setPagination((p) => ({ ...p, current: 1 }));
          }}
          placeholder={["From", "To"]}
        />

        <Button
          onClick={() => handleApplySearch(searchKeyword)}
          type="primary"
          loading={loading}
        >
          Apply Filters
        </Button>
        <Button onClick={handleClear}>Clear</Button>
      </div>

      <Row gutter={16}>
        <Col xs={24} lg={24}>
          <div className="table-responsive"  >
            <Table
              rowKey="id"
              loading={loading}
              columns={columns}
              dataSource={displayedUsers}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: totalUsers,
                showSizeChanger: true,
              }}
              onChange={(p) => {
                setPagination((prev) => ({
                  ...prev,
                  current: p.current,
                  pageSize: p.pageSize,
                }));
              }}
              locale={{
                emptyText: (
                  <div style={{ padding: "24px 0", textAlign: "center" }}>
                    No users found
                  </div>
                ),
              }}
            />
          </div>
        </Col>
      </Row>

      <Modal
        open={!!selectedUser}
        title="User Profile Details"
        footer={null}
        onCancel={() => setSelectedUser(null)}
        destroyOnClose
      >
        {!selectedUser ? null : (
          <>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="Full Name">
                {selectedUser.name || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Email address">
                {selectedUser.email || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Username">
                {selectedUser.username || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Role">
                {selectedUser.role || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Account status">
                {statusLabel(selectedUser)}
              </Descriptions.Item>
              <Descriptions.Item label="Signup date">
                {selectedUser.createdAt
                  ? moment(selectedUser.createdAt).format("YYYY-MM-DD")
                  : "—"}
              </Descriptions.Item>
            </Descriptions>

            {selectedUser.profilePic ? (
              <div style={{ marginTop: 16, textAlign: "center" }}>
                <img
                  src={selectedUser.profilePic}
                  alt="User profile"
                  style={{
                    width: 120,
                    height: 120,
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "1px solid rgba(255,255,255,0.15)",
                  }}
                />
              </div>
            ) : null}

            {selectedUser.billingActive && !selectedUser.cancelAtPeriodEnd && (
              <div style={{ marginTop: 24, textAlign: "center" }}>
                <Button
                  danger
                  type="primary"
                  loading={cancelingSubUserId === selectedUser.id}
                  onClick={() => handleCancelSubscription(selectedUser)}
                >
                  Cancel at end of billing period
                </Button>
              </div>
            )}
          </>
        )}
      </Modal>

      {loading && users.length === 0 ? (
        <div style={{ display: "none" }} />
      ) : null}
    </Card>
  );
};

export default UserManager;
